import { useEffect, useRef } from "react";
import { SOUND_ASSETS } from "@/exported/pages/Configuration/NotificationSettings/components/soundAssets";

// Mounted by the shell root App, never by an MFE. The main process sends
// "play-notification-sound" to ONE window (sendToMainScreen) and does not queue
// it, so the listener has to belong to something that is mounted on every
// route. It used to live in the tide MFE's App, which single-spa only mounts on
// /tide/* — every alarm raised while the main screen sat on /marlin-v2, /manual
// or /welcome was dropped in silence. Mounting it here AND in an MFE would
// register two listeners in the same document and play the sound twice.
// The shell types window.electronAPI as `unknown` (env.d.ts) so it never has to
// depend on the desktop's type package; narrow it here the same way
// shell-handle/desktopAuthSync.ts does.
type ElectronSoundApi = {
  onPlayNotificationSound?: (
    callback: (event: unknown, data: { sound: string }) => void,
  ) => () => void;
};

function electronApi(): ElectronSoundApi | undefined {
  return (window as unknown as { electronAPI?: ElectronSoundApi }).electronAPI;
}

export const useNotificationSound = () => {
  const audioRef = useRef<{ [key: string]: HTMLAudioElement }>({});

  useEffect(() => {
    const entries = Object.entries(SOUND_ASSETS);
    const audioMap: { [key: string]: HTMLAudioElement } = {};

    entries.forEach(([key, src]) => {
      audioMap[key] = new Audio(src);
    });

    audioRef.current = audioMap;

    Object.values(audioRef.current).forEach((audio) => {
      audio.volume = 1.0;
      audio.muted = false;

      audio.addEventListener("error", (e) => {
        console.error("[Sound] Failed to load audio:", e, audio.src);
      });

      audio.load();
    });

    return () => {
      Object.values(audioRef.current).forEach((audio) => {
        audio.pause();
        audio.src = "";
      });
    };
  }, []);

  useEffect(() => {
    const electron = electronApi();

    if (!electron?.onPlayNotificationSound) {
      return;
    }

    return electron.onPlayNotificationSound((_event, data) => {
      const { sound } = data;

      if (sound === "MUTE" || !audioRef.current[sound]) {
        return;
      }

      const audio = audioRef.current[sound];

      audio.volume = 1.0;
      audio.muted = false;
      audio.currentTime = 0;

      // An unhandled rejection here is invisible: a blocked autoplay or a 404
      // asset silences every alarm with no console entry and no log line.
      audio.play().catch((error) => {
        console.error("[Sound] Failed to play alarm:", sound, audio.src, error);
      });
    });
  }, []);
};
